import React from 'react';
import PropTypes from 'prop-types';
import { Popover, Row, Col, Label } from 'react-bootstrap';
import { Field } from 'redux-form';
import CheckBoxSelect from 'components/atoms/form/CheckBoxSelect';
import FormSectionHeading from 'components/atoms/form/FormSectionHeading';
import CurrentImportantSettingsContainer from 'containers/forms/entities/CurrentImportantSettingsContainer';
import { DisabledDiv, HiddenBorderDiv } from 'components/atoms/form/UtilityDivs';
import StyledLabel from 'components/atoms/inputs/Label';
import InputTip from 'components/atoms/form/InputTip';

const overrideBrandAdvancedlHelpText = (<Popover
    id="override-brand-total-activities-setting"
    placement="right"
    title="Override Brand Advanced Setting"
>
    You can use this to specifically switch an Entity to show/not show advanced settings regardless of Brand settings.
</Popover>);

const enforceSummaryOnlyDashboardViewHelpText = (<Popover
    id="enforce-summary-only-dashboard-view"
    placement="right"
    title="Summary Only View"
>
    Summary-only dashboard that displays data from the channels checked below.
</Popover>);

const ecpaSummaryViewHelpText = (<Popover
    id="ecpa-summary-conversions"
    placement="right"
    title="Show eCPA on Summary Channel"
>
    Enabling this option will include eCPA conversions in the summary channel.
    This currently includes the Social and SEM channels as well.
</Popover>);

const showTtdSitesSummaryHelpText = (
    <Popover
        id={'site-summary-data'}
        placement={'right'}
        title={'Show Channel Placement Distributions'}
    >
        Enabling this will show an additional channel breakdown table that shows impressions and completions for ATV and
        video ads broken out by the channels on which they are shown.
    </Popover>
);

const showSocialExpandModulesHelpText = (<Popover
    id="show-social-expand-modules"
    placement="right"
    title="Show Facebook Expand Modules on Social Channel"
>
    Enabling this option will include all Facebook expand modules in the social
    channel broken out by campaign objective.
</Popover>);


const shouldRenderTotalActivitiesOverlay = (props) => {
    if (props.selectedBrandId) {
        return !props.selectOverrideBrandAdvancedOptions;
    }
    return false;
};

const AdvancedSettings = props => (
    <Row>
        <Col xs={12}>
            <CurrentImportantSettingsContainer>
                {({
                    selectedBrandId,
                    selectOverrideBrandAdvancedOptions
                }) => (
                    <HiddenBorderDiv hide={!selectedBrandId}>
                        {selectedBrandId ? (
                            <Field
                                name="meta.overrideBrand.advancedOptions"
                                label="Override Brand Advanced Options"
                                type="checkbox"
                                component={CheckBoxSelect}
                                overlay={overrideBrandAdvancedlHelpText}
                            />
                        ) : null}
                        <DisabledDiv sm={6} disablePointer={shouldRenderTotalActivitiesOverlay(
                            {
                                ...props,
                                selectedBrandId,
                                selectOverrideBrandAdvancedOptions
                            })}>
                            <Row>
                                <Col xs={12}>
                                    <FormSectionHeading marginTop={0}>
                                        Advanced Options
                                    </FormSectionHeading>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={12}>
                                    <StyledLabel margin='10px 0 5px 0'>TTD/DCM/DV360 Only</StyledLabel>
                                    <br />
                                    <Field
                                        component={CheckBoxSelect}
                                        label="Show Breakdown by Targeting Tactic"
                                        name={'meta.showTargetingTactics'}
                                        type="checkbox"
                                    />
                                    <br />
                                    <Field
                                        component={CheckBoxSelect}
                                        label="Include non-eCPA Activities (Total Activities)"
                                        name={'meta.showTotalActivities'}
                                        type="checkbox"
                                    />
                                    <br />
                                    <StyledLabel margin='10px 0 5px 0'>Show Data</StyledLabel>
                                    <br />
                                    <Field
                                        component={CheckBoxSelect}
                                        label="Show Conversion Revenue"
                                        name="meta.showConversionRevenue"
                                        type="checkbox"
                                    />
                                    <br />
                                    <Field
                                        component={CheckBoxSelect}
                                        label="Show SEM Cost per Conversion"
                                        name={'meta.showSemCpconv'}
                                        type="checkbox"
                                    />
                                    <br />
                                    <Field
                                        component={CheckBoxSelect}
                                        label="Show Social Cost per Conversion"
                                        name={'meta.showSocialCpconv'}
                                        type="checkbox"
                                    />
                                    <br />
                                    <Field
                                        component={CheckBoxSelect}
                                        label="Show Facebook Demographic Data"
                                        name={'meta.showFacebookDemographicData'}
                                        type="checkbox"
                                    />
                                    <br />
                                    <Field
                                        component={CheckBoxSelect}
                                        label="Show Facebook Expand Modules"
                                        name={'meta.showSocialExpandModules'}
                                        type="checkbox"
                                        overlay={showSocialExpandModulesHelpText}
                                    />
                                    <br />
                                    <Field
                                        component={CheckBoxSelect}
                                        label="Show eCPA on Summary Channel"
                                        name={'meta.showeCPAonSummaryDashboard'}
                                        type="checkbox"
                                        overlay={ecpaSummaryViewHelpText}
                                    />
                                    <InputTip marginTop="-10px" marginBottom="-5px">Heads up, this includes SEM and Social conversions</InputTip>
                                    <br />
                                    <Field
                                        component={CheckBoxSelect}
                                        label={
                                            <React.Fragment>
                                                Show Video/ATV Channel Distributions
                                                <Label bsStyle="primary" style={{ marginLeft: '5px', verticalAlign: 'middle' }}>Beta</Label>
                                            </React.Fragment>
                                        }
                                        name={'meta.showTtdSitesSummary'}
                                        type="checkbox"
                                        overlay={showTtdSitesSummaryHelpText}
                                    />
                                    <br />
                                    <StyledLabel margin='10px 0 5px 0'>Additional Functionality</StyledLabel>
                                    <br />
                                    <Field
                                        component={CheckBoxSelect}
                                        label="Enable Objective Switching"
                                        name={'meta.enableObjectiveSwitching'}
                                        type="checkbox"
                                    />
                                    <br />
                                    <Field
                                        component={CheckBoxSelect}
                                        label="Enable Summary Mode: Removes links to channel specific detail pages."
                                        name="meta.enforceSummaryOnlyDashboardView"
                                        type="checkbox"
                                        overlay={enforceSummaryOnlyDashboardViewHelpText}
                                    />
                                </Col>
                            </Row>
                        </DisabledDiv>
                    </HiddenBorderDiv>
                )}
            </CurrentImportantSettingsContainer>
        </Col>
    </Row>
);

AdvancedSettings.propTypes = {
    selectedBrandId: PropTypes.number,
    selectOverrideBrandAdvancedOptions: PropTypes.bool
};

export default AdvancedSettings;



// WEBPACK FOOTER //
// ./src/components/organisms/entity/edit/AdvancedSettings.js